<script setup lang="ts">
import {
  InfoCircle,
  SearchMd,
  Lock01,
  LockUnlocked01,
  Truck02,
} from "utruckin-icons";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { useTeamStore } from "@/features/teams/team.store";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { ITeam } from "@/features/teams/team.types";
import { DateTime } from "luxon";
import { useCompanyStore } from "@/features/company/company.store";
import { ICompany } from "@/features/company/company.types";
import CommonButton from "@/components/common/CommonButton.vue";
import CustomTableDropdown from "@/components/custom/CustomTableDropdown.vue";
import { useAppStore } from "@/store/app.store";
import { useProvidersStore } from "@/features/providers/providers.store";
import { IProvider } from "@/features/providers/providers.types";
import CustomTab from "@/components/custom/CustomTab.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useNotification } from "@/utils/notification";

const teamStore = useTeamStore();
const companyStore = useCompanyStore();
const providerStore = useProvidersStore();
const appStore = useAppStore();

const { pushNotification } = useNotification();

const teams = ref<ITeam[]>([]);
const team = ref<string | null>(null);
const provider = ref<string | null>(null);
const companyLoading = ref(false);
const teamLoading = ref(false);
const providerLoading = ref(false);
const search = ref<string>("");
const setTeam = ref<string | null>(null);
const selectedItems = ref([]);
const providers = ref<IProvider[]>([]);
const companyTable = ref();
const companies = ref<ICompany[]>([]);
const activeTab = ref<number>(0);
const tabs = [
  { label: "All", value: 0 },
  { label: "With team", value: 1 },
  { label: "No team", value: 2 },
];

const isOpenModal = ref<boolean>(false);
const note = ref<string>("");
const modalItem = ref<ICompany | null>(null);

const reason = ref<string>("");
const lockModalItem = ref<any>();
const lockModal = ref<boolean>(false);
const lockModalLoading = ref<boolean>(false);

let searchTimer: ReturnType<typeof setTimeout> | null = null;

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const providerItems = computed(() =>
  providers.value.map((t) => ({ value: t.id, name: t.name }))
);

const openModal = (item: ICompany) => {
  modalItem.value = item;
  isOpenModal.value = true;
  note.value = item.note;
};

const closeModal = () => {
  modalItem.value = null;
  isOpenModal.value = false;
  note.value = "";
};

const saveModal = async () => {
  if (!modalItem.value) return;

  await companyStore.addCompanyNote(
    companyLoading,
    modalItem.value.id,
    { note: note.value },
    async (data: any) => {
      if (data) {
        pushNotification("Successfully saved", "success");
        closeModal();
        await getList();
      }
    }
  );
};

const updateTeam = async (item: ICompany, value: string) => {
  const rowId = item.id;
  item.team_id = value;
  const data = await companyStore.updateCompany(companyLoading, item.id, {
    team_id: value as string,
  });
  if (data) {
    await getList();
  }
  companyTable.value?.scrollToRow(rowId);
};

function datepickerToUtcDateOnly(d: Date | null | undefined) {
  return DateTime.fromISO(d, {
    zone: "utc",
  })
    .setZone("America/New_York")
    .toFormat("yyyy-MM-dd");
}

const getList = async () => {
  [teams.value, companies.value, providers.value] = await Promise.all([
    teamStore.getTeams(teamLoading),
    companyStore.getCompanies(companyLoading),
    providerStore.getProviders(providerLoading),
  ]);
};

const updateAllTeam = async () => {
  let company_ids: string[] = [];

  selectedItems.value.forEach((item: ICompany) => {
    company_ids.push(item.id);
  });

  let result = {
    company_ids: company_ids,
    team_id: setTeam.value as string,
  };
  const data = await companyStore.bulkUpdateCompany(teamLoading, result);

  if (data) {
    window.location.reload();
  }
};

const unlock = async (item: ICompany) => {
  await companyStore.taskCreationBlock(
    lockModalLoading,
    item.id,
    {
      reason: item.task_creation_blocked_reason,
      is_task_creation_blocked: false,
    },
    async (data: any) => {
      if (data) {
        pushNotification("Successfully unlocked", "success");
        await getList();
      }
    }
  );
};

const blockItem = (item: ICompany) => {
  lockModal.value = true;
  lockModalItem.value = item;
  reason.value = "";
};

const closeLockModal = () => {
  lockModalItem.value = null;
  lockModal.value = false;
  reason.value = "";
};

const saveLockModal = async () => {
  if (!lockModalItem.value) return;

  await companyStore.taskCreationBlock(
    lockModalLoading,
    lockModalItem.value.id,
    {
      reason: reason.value,
      is_task_creation_blocked: true,
    },
    async (data: any) => {
      if (data) {
        pushNotification("Successfully locked", "success");
        closeLockModal();
        await getList();
      }
    }
  );
};

const deactivateTruck = async (item: any) => {
  if (companyLoading.value) return;

  await companyStore.truckDeactivation(
    companyLoading,
    item.id,
    {
      truck_deactivation_enabled: !item.truck_deactivation_enabled,
    },
    async (data: any) => {
      if (data) {
        pushNotification("Successfully saved", "success");
        await getList();
      }
    }
  );
};

onMounted(() => {
  companyStore.commonFilter.team_id = "";

  getList();
  if (companyStore.commonFilter?.has_team == null) {
    activeTab.value = 0;
  } else if (companyStore.commonFilter?.has_team) {
    activeTab.value = 1;
  } else {
    activeTab.value = 2;
  }
});

onUnmounted(() => {
  companyStore.clearFilter();
});

watch(search, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(async () => {
    companyStore.commonFilter.search = val;
    await getList();
  }, 400);
});

watch(team, async (val) => {
  companyStore.commonFilter.team_id = val as string;
  await getList();
});

watch(provider, async (val) => {
  companyStore.commonFilter.provider_id = val as string;
  await getList();
});

watch(
  () => activeTab.value,
  async (newVal) => {
    if (companyStore.commonFilter) {
      if (newVal == 0) {
        companyStore.commonFilter.has_team = null;
      } else companyStore.commonFilter.has_team = newVal == 1;

      await getList();
    }
  }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);
</script>

<template>
  <div class="v-company">
    <div class="header">
      <div class="header__filter">
        <CommonSearch
          v-model="search"
          :icon="SearchMd"
          placeholder="Search"
          clearable
          :disabled="selectedItems.length > 0"
        />
        <CommonDropdown
          v-model="team"
          :items="teamItems"
          label="Team"
          clearable
          :disabled="selectedItems.length > 0"
        />
        <CommonDropdown
          v-model="provider"
          :items="providerItems"
          label="App name"
          clearable
          :disabled="selectedItems.length > 0"
        />
        <CustomTab :tabs="tabs" v-model="activeTab" />
      </div>

      <div class="header__stats" v-if="selectedItems.length > 0">
        <CommonDropdown
          v-model="setTeam"
          :items="teamItems"
          label="Team"
          clearable
        />

        <CommonButton @click="updateAllTeam" type="primary" label="Save" />
      </div>
      <div v-else class="header__info">
        <div class="capsule">
          <div class="label">All truck</div>
          <div class="value">{{ companies.active_trucks_summary?.total }}</div>
        </div>

        <div
          class="capsule"
          v-for="(item, index) in companies.active_trucks_summary?.by_team"
          :key="index"
        >
          <div class="label">{{ item.team_name }}</div>
          <div class="value">{{ item.active_trucks }}</div>
        </div>
      </div>
    </div>

    <CommonTable
      ref="companyTable"
      :headers="companyStore.companyHeaders"
      :loading="companyLoading"
      :items="companies.items"
      v-model:selected="selectedItems"
      selectMode
    >
      <template #id="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <template #created_time="{ item }">
        {{ datepickerToUtcDateOnly(item.created_time) }}
      </template>

      <template #team="{ item }">
        <CustomTableDropdown
          :model-value="item.team_id"
          @update:model-value="(value: any) => updateTeam(item, value)"
          :items="teamItems"
          label="Team"
          position="bottom"
          clearable
          :disabled="selectedItems.length > 0"
        />
      </template>

      <template #actions="{ item }">
        <div class="action">
          <div
            class="action__item"
            @click="openModal(item)"
            v-tooltip="{
              content: 'Comment',
              html: true,
            }"
          >
            <InfoCircle :size="18" />
          </div>

          <div
            v-if="item.is_task_creation_blocked"
            class="action__item unlock"
            @click="unlock(item)"
            v-tooltip="{
              content: item.task_creation_blocked_reason,
              html: true,
            }"
          >
            <LockUnlocked01 :size="18" />
          </div>

          <div
            v-else
            class="action__item lock"
            @click="blockItem(item)"
            v-tooltip="{
              content: 'Lock',
              html: true,
            }"
          >
            <Lock01 :size="18" />
          </div>

          <div
            class="action__item"
            :class="item.truck_deactivation_enabled ? '' : 'unlock'"
            @click="deactivateTruck(item)"
            v-tooltip="{
              content: item.truck_deactivation_enabled
                ? 'Truck deactivation enabled'
                : 'Truck deactivation disabled',
              html: true,
            }"
          >
            <Truck02 :size="18" />
          </div>
        </div>
      </template>
    </CommonTable>

    <CommonModal v-model="isOpenModal" title="Company note">
      <div class="new-modal">
        <CommonInput v-model="note" label="Note" multiline />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton type="outline" label="Cancel" @clicked="closeModal" />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>

    <CommonModal v-model="lockModal" title="Block task creation">
      <div class="new-modal">
        <CommonInput v-model="reason" label="Reason" multiline />
      </div>

      <div class="auto-complete">
        <div class="item" @click="reason = 'Payment overdue'">
          Payment overdue
        </div>
        <div class="item" @click="reason = 'No edit'">No edit</div>
        <div class="item" @click="reason = 'Left our service'">
          Left our service
        </div>
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="closeLockModal"
          />
          <CommonButton type="primary" label="Save" @clicked="saveLockModal" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-company {
  display: flex;
  flex-direction: column;
  padding: var(--base-space-24);
  height: calc(100vh - 80px);
  background: var(--color-system-background);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__filter {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__stats {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__info {
      display: flex;
      align-items: center;
      gap: 8px;

      .capsule {
        display: flex;
        border: 1px solid var(--color-border);
        border-radius: 14px;
        overflow: hidden;
        color: var(--color-label);

        .label {
          padding: 12px 8px 12px 12px;
        }

        .value {
          padding: 12px 12px 12px 8px;
          background: var(--color-secondary-system-background);
        }
      }
    }
  }

  .body {
    padding: 0 16px;
  }

  .action {
    display: flex;
    gap: 8px;

    &__item {
      width: 32px;
      height: 32px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-secondary-system-background);

      //&.lock {
      //  color: var(--color-system-green);
      //}
      //
      &.unlock {
        color: var(--color-system-red);
      }
    }
  }
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}

.auto-complete {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: var(--base-space-8);
  flex-wrap: wrap;

  .item {
    cursor: pointer;
    border: 1px solid var(--color-border);
    color: var(--color-label);
    padding: 10px 14px;
    border-radius: 14px;
    background: var(--color-secondary-system-background);
  }
}
</style>
